<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');
const { accountId } = useAccount();
const tables = ref([]);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

const OPS_TEXT = computed(() => [
  { value: 'contains',     label: t('FLOW_BUILDER.COMMON.OP_CONTAINS') },
  { value: 'not_contains', label: t('FLOW_BUILDER.COMMON.OP_NOT_CONTAINS') },
  { value: 'equal_to',     label: t('FLOW_BUILDER.COMMON.OP_EQUAL_TO') },
  { value: 'not_equal_to', label: t('FLOW_BUILDER.COMMON.OP_NOT_EQUAL_TO') },
  { value: 'starts_with',  label: t('FLOW_BUILDER.COMMON.OP_STARTS_WITH') },
  { value: 'ends_with',    label: t('FLOW_BUILDER.COMMON.OP_ENDS_WITH') },
  { value: 'is_present',   label: t('FLOW_BUILDER.COMMON.OP_IS_PRESENT') },
  { value: 'is_empty',     label: t('FLOW_BUILDER.COMMON.OP_IS_EMPTY') },
]);

const OPS_NUMBER = computed(() => [
  { value: 'equal_to',                 label: t('FLOW_BUILDER.COMMON.OP_EQUAL_TO') },
  { value: 'not_equal_to',             label: t('FLOW_BUILDER.COMMON.OP_NOT_EQUAL_TO') },
  { value: 'greater_than',             label: t('FLOW_BUILDER.COMMON.OP_GREATER_THAN') },
  { value: 'less_than',                label: t('FLOW_BUILDER.COMMON.OP_LESS_THAN') },
  { value: 'greater_than_or_equal_to', label: t('FLOW_BUILDER.COMMON.OP_GREATER_THAN_OR_EQUAL_TO') },
  { value: 'less_than_or_equal_to',    label: t('FLOW_BUILDER.COMMON.OP_LESS_THAN_OR_EQUAL_TO') },
]);

const OPS_SELECT = computed(() => [
  { value: 'equal_to',     label: t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.OP_MATCHES') },
  { value: 'not_equal_to', label: t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.OP_NOT_MATCHES') },
  { value: 'is_present',   label: t('FLOW_BUILDER.COMMON.OP_IS_PRESENT') },
  { value: 'is_empty',     label: t('FLOW_BUILDER.COMMON.OP_IS_EMPTY') },
]);

const NO_VALUE_OPS = new Set(['is_present', 'is_empty']);

const QUERY_OPTIONS = computed(() => [
  { id: 'and', name: t('FLOW_BUILDER.COMMON.AND') },
  { id: 'or',  name: t('FLOW_BUILDER.COMMON.OR') },
]);

/* global axios */
async function loadSchema() {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/flow_builder/query_database/schema`
    );
    tables.value = data.tables ?? [];
  } catch {
    tables.value = [];
  }
}

onMounted(loadSchema);

const selectedTable = computed(() => props.modelValue?.table ?? '');
const conditions    = computed(() => props.modelValue?.conditions ?? []);
const selectedCols  = computed(() => props.modelValue?.columns ?? []);
const limitVal      = computed(() => props.modelValue?.limit ?? 10);
const variableName  = computed(() => props.modelValue?.variable_name ?? '');
const resultVarToken = computed(() => `{{variables.${variableName.value || 'query_result'}}}`);
const countVarToken  = computed(() => `{{variables.${variableName.value || 'query_result'}_count}}`);

const tableOptions  = computed(() => tables.value.map(t => ({ id: t.key, name: resolveLabel(t.label) })));
const tableSelected = computed(() => tableOptions.value.find(t => t.id === selectedTable.value) ?? null);
const currentTable  = computed(() => tables.value.find(t => t.key === selectedTable.value) ?? null);
const columnDefs    = computed(() => currentTable.value?.columns ?? []);
const colOptions    = computed(() => columnDefs.value.map(c => ({ id: c.key, name: resolveLabel(c.label) })));

const selectColOptions = computed(() =>
  (currentTable.value?.select_columns ?? []).map(c => ({ value: c, label: c }))
);

function colDef(key)   { return columnDefs.value.find(c => c.key === key) ?? null; }
function needsValue(c) { return !NO_VALUE_OPS.has(c.filter_operator); }
function isSelect(key) { return colDef(key)?.type === 'select'; }

function opsForKey(key) {
  const def = colDef(key);
  if (!def) return OPS_TEXT.value;
  if (def.type === 'number') return OPS_NUMBER.value;
  if (def.type === 'select') return OPS_SELECT.value;
  return OPS_TEXT.value;
}

function opOptions(key)  { return opsForKey(key).map(o => ({ id: o.value, name: o.label })); }
function valOptions(key) { return (colDef(key)?.options ?? []).map(o => ({ id: o.key, name: resolveLabel(o.label) })); }
function selectedOp(c)   { return opOptions(c.attribute_key).find(o => o.id === c.filter_operator) ?? null; }
function selectedVal(c)  { return valOptions(c.attribute_key).find(o => o.id === c.values) ?? null; }

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

function onSelectTable(opt) {
  update({ table: opt?.id ?? '', conditions: [], columns: [] });
}

function emitConditions(list) {
  const updated = list.map((c, i) => ({
    ...c,
    query_operator: i < list.length - 1 ? (c.query_operator || 'and') : null,
  }));
  update({ conditions: updated });
}

function addCondition() {
  const first = columnDefs.value[0];
  if (!first) return;
  emitConditions([
    ...conditions.value,
    {
      attribute_key: first.key,
      filter_operator: opsForKey(first.key)[0]?.value ?? 'equal_to',
      values: '',
      query_operator: 'and',
    },
  ]);
}

function removeCondition(i) {
  emitConditions(conditions.value.filter((_, idx) => idx !== i));
}

function patchCondition(i, obj) {
  emitConditions(conditions.value.map((c, idx) => (idx === i ? { ...c, ...obj } : c)));
}

function onSelectColumn(i, val) {
  const def = columnDefs.value.find(c => c.key === val?.id);
  if (!def) return;
  patchCondition(i, {
    attribute_key: def.key,
    filter_operator: opsForKey(def.key)[0]?.value ?? 'equal_to',
    values: '',
  });
}

// Variable picker
const openPickerIndex = ref(null);
const variableSearch  = ref('');

const filteredVars = computed(() => {
  const q = variableSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q));
});

function togglePicker(i) {
  openPickerIndex.value = openPickerIndex.value === i ? null : i;
  variableSearch.value = '';
}

function insertVariable(i, key) {
  patchCondition(i, { values: `{{${key}}}` });
  openPickerIndex.value = null;
  variableSearch.value = '';
}
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Tabela -->
    <div class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.TABLE') }}</span>
      <MultiselectDropdown
        :options="tableOptions"
        :selected-item="tableSelected"
        :multiselector-placeholder="t('FLOW_BUILDER.COMMON.SELECT_TABLE')"
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH_TABLE')"
        :has-thumbnail="false"
        :hide-search="true"
        @select="onSelectTable"
      />
    </div>

    <!-- Condições WHERE -->
    <template v-if="selectedTable">
      <div class="h-px bg-n-weak -mx-4" />

      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.FILTERS') }}</span>

        <div class="flex flex-col gap-3">
          <template v-for="(cond, i) in conditions" :key="i">

            <!-- AND/OR connector -->
            <div v-if="i > 0" class="flex items-center gap-2">
              <div class="h-px flex-1 bg-n-weak" />
              <div class="multiselect-wrap--small w-16">
                <MultiselectDropdown
                  :options="QUERY_OPTIONS"
                  :selected-item="QUERY_OPTIONS.find(o => o.id === (conditions[i - 1].query_operator || 'and'))"
                  :has-thumbnail="false"
                  :hide-search="true"
                  @select="v => patchCondition(i - 1, { query_operator: v?.id ?? 'and' })"
                />
              </div>
              <div class="h-px flex-1 bg-n-weak" />
            </div>

            <!-- Condition card -->
            <div class="rounded-xl border border-n-weak bg-n-solid-1">

              <!-- Row 1: coluna + delete -->
              <div class="flex items-center gap-1.5 px-2 py-1.5 bg-n-solid-2 border-b border-n-weak rounded-t-xl">
                <span class="text-[11px] font-medium text-n-slate-9 shrink-0">{{ t('FLOW_BUILDER.COMMON.IF') }}</span>
                <div class="multiselect-wrap--small flex-1">
                  <MultiselectDropdown
                    :options="colOptions"
                    :selected-item="colOptions.find(o => o.id === cond.attribute_key) ?? null"
                    :multiselector-placeholder="t('FLOW_BUILDER.COMMON.COLUMN')"
                    :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                    :has-thumbnail="false"
                    @select="v => onSelectColumn(i, v)"
                  />
                </div>
                <button
                  type="button"
                  class="i-lucide-x size-3 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors"
                  @click="removeCondition(i)"
                />
              </div>

              <!-- Row 2: operador + valor -->
              <div class="flex items-start gap-1.5 px-2 py-1.5">
                <!-- Operador -->
                <div class="multiselect-wrap--small flex-1 min-w-0">
                  <MultiselectDropdown
                    :options="opOptions(cond.attribute_key)"
                    :selected-item="selectedOp(cond)"
                    :multiselector-placeholder="t('FLOW_BUILDER.COMMON.OPERATOR')"
                    :has-thumbnail="false"
                    :hide-search="true"
                    @select="v => patchCondition(i, { filter_operator: v?.id ?? '' })"
                  />
                </div>

                <!-- Valor -->
                <template v-if="needsValue(cond)">
                  <!-- select type column -->
                  <div v-if="isSelect(cond.attribute_key)" class="multiselect-wrap--small flex-1">
                    <MultiselectDropdown
                      :options="valOptions(cond.attribute_key)"
                      :selected-item="selectedVal(cond)"
                      :multiselector-placeholder="t('FLOW_BUILDER.COMMON.VALUE_PLACEHOLDER')"
                      :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                      :has-thumbnail="false"
                      @select="v => patchCondition(i, { values: v?.id ?? '' })"
                    />
                  </div>
                  <!-- text / number column: input group with variable button -->
                  <div v-else class="relative flex-1 min-w-0">
                    <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                      <input
                        type="text"
                        class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                        :value="cond.values"
                        :placeholder="t('FLOW_BUILDER.COMMON.VALUE_PLACEHOLDER')"
                        @input="patchCondition(i, { values: $event.target.value })"
                      />
                      <button
                        type="button"
                        class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
                        :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
                        @click.stop="togglePicker(i)"
                      >
                        <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
                      </button>
                    </div>
                    <!-- picker dropdown -->
                    <div
                      v-if="openPickerIndex === i"
                      class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
                    >
                      <div class="px-2 pt-2 pb-1">
                        <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                          <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                          <input
                            v-model="variableSearch"
                            type="text"
                            class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                            :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                            autofocus
                          />
                        </div>
                      </div>
                      <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                        <button
                          v-for="v in filteredVars"
                          :key="v.key"
                          type="button"
                          class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                          @click="insertVariable(i, v.key)"
                        >
                          {{ resolveLabel(v.label) }}
                        </button>
                        <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">
                          {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
                        </p>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

            </div>
          </template>

          <Button
            ghost
            slate
            sm
            icon="i-lucide-plus"
            :label="t('FLOW_BUILDER.COMMON.ADD_FILTER')"
            class="self-start"
            @click="addCondition"
          />
        </div>
      </div>

      <div class="h-px bg-n-weak -mx-4" />

      <!-- Colunas a retornar -->
      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.COLUMNS_TO_RETURN') }}</span>
        <TagMultiSelectComboBox
          :model-value="selectedCols"
          :options="selectColOptions"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.ALL_COLUMNS')"
          :search-placeholder="t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.SEARCH_COLUMN')"
          :empty-state="t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.NO_COLUMN_FOUND')"
          @update:model-value="v => update({ columns: v })"
        />
      </div>

      <!-- Limite -->
      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.RECORDS_LIMIT') }}</span>
        <input
          type="number"
          min="1"
          max="100"
          class="w-24 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          :value="limitVal"
          @input="update({ limit: parseInt($event.target.value) || 10 })"
        />
      </div>

      <!-- Variável -->
      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.SAVE_RESULT_IN_VARIABLE') }}</span>
        <input
          type="text"
          class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.VARIABLE_NAME_PLACEHOLDER')"
          :value="variableName"
          @input="update({ variable_name: $event.target.value })"
        />
        <p class="text-xs text-n-slate-9">
          {{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.USE_HINT_BEFORE') }}
          <code class="text-n-slate-11">{{ resultVarToken }}</code>
          {{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.USE_HINT_MIDDLE') }}
          <code class="text-n-slate-11">{{ countVarToken }}</code>{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.USE_HINT_AFTER') }}
        </p>
      </div>

      <!-- Outputs info -->
      <div class="flex flex-col gap-1.5 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
        <div class="flex items-center gap-2 text-xs text-n-slate-9">
          <span class="inline-block size-2 rounded-full bg-n-teal-9 shrink-0" />
          <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.FOUND') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.FOUND_HINT') }}</span>
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-9">
          <span class="inline-block size-2 rounded-full bg-n-ruby-9 shrink-0" />
          <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.NOT_FOUND') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.QUERY_DATABASE.NOT_FOUND_HINT') }}</span>
        </div>
      </div>
    </template>

  </div>
</template>
